<template>
    <div>
        <v-card
            v-if="payment != null && payment.state === 'open'"
            :class="{ 'max_width': !fluid }"
            style="margin: auto;"
        >
            <v-card-title class="pb-0">
                <v-icon style="margin-right: 10px">
                    mdi-credit-card
                </v-icon>
                <h2 class="payment-heading">{{ payment_title }}</h2>
            </v-card-title>

            <v-card-text>
                <v-btn
                    v-if="accept_ach"
                    color="primary"
                    class="text-decoration-underline pa-0 mt-2"
                    text
                    @click="$emit('swich-to-pay-by-ach')"
                >
                    Pay using bank transfer (ACH) instead
                </v-btn>

                <v-row
                    class="mt-3"
                    dense
                >
                    <v-col
                        cols="12"
                        sm="6"
                    >
                        <rc-base-input
                            type="text"
                            label="Name on Card"
                            v-model="name_on_card"
                            autocomplete="cc-name"
                            required
                            @input="onInput"
                        />
                    </v-col>

                    <v-col
                        cols="12"
                        sm="6"
                    >
                        <rc-base-input
                            type="text"
                            mode="numeric"
                            label="Card Number"
                            v-model="plain_text_card_number"
                            autocomplete="cc-number"
                            required
                            @input="onInput"
                        />
                    </v-col>
                </v-row>

                <v-row dense>
                    <v-col>
                        <rc-base-input
                            type="text"
                            mode="numeric"
                            label="Expiration"
                            hint="MM/YY"
                            autocomplete="cc-exp"
                            v-model="expiration"
                            required
                            @input="onInput"
                        />
                    </v-col>

                    <v-col>
                        <rc-base-input
                            type="text"
                            mode="numeric"
                            label="CCV"
                            v-model="plain_text_ccv"
                            autocomplete="cc-csc"
                            required
                            @input="onInput"
                        />
                    </v-col>

                </v-row>

                <v-row dense>
                    <v-col>
                        <rc-base-input
                            type="text"
                            mode="numeric"
                            label="Billing Zip / Postal Code"
                            v-model="billing_zip_code"
                            autocomplete="postal-code"
                            required
                            @input="onInput"
                        />
                    </v-col>
                </v-row>

                <v-row
                    v-if="show_email"
                    dense
                >
                    <v-col>
                        <rc-base-input
                            type="email"
                            label="Email Address"
                            v-model="email_address"
                            :required="isFieldRequired('email')"
                            @input="onInput"
                        />
                    </v-col>
                </v-row>

                <v-row
                    v-if="show_phone"
                    dense
                >
                    <v-col>
                        <rc-base-input
                            type="tel"
                            label="Phone Number"
                            v-model="phone_number"
                            :required="isFieldRequired('phone')"
                            @input="onInput"
                            @validated="phone_input_valid = $event"
                        />
                    </v-col>
                </v-row>

                <v-row
                    v-if="payment.request.age_verification_requirement === 'checkbox'"
                    dense
                >
                    <v-col>
                        <v-checkbox
                            :label="'By checking the box, you affirm that you are over the age of ' + min_age_in_years + '.'"
                            v-model="age_verification_checked"
                            tabindex="0"
                            required
                            @change="onInput"
                        />
                    </v-col>
                </v-row>

                <v-row
                    v-if="payment.request.age_verification_requirement === 'birthdate'"
                    dense
                >
                    <v-col>
                        <rc-base-input
                            type="birthday"
                            label="Date of Birth"
                            v-model="age_verification_birthdate"
                            required
                            @input="onInput"
                            @validated="birthday_input_valid = $event"
                        />
                    </v-col>
                </v-row>

                <v-row v-if="show_pay_button">
                    <v-col>
                        <v-btn
                            ref="pay-button"
                            style="width: 100%;"
                            class="white--text"
                            color="#5D647C"
                            @click="onPay"
                        >
                            {{ payment_text }}
                        </v-btn>
                    </v-col>
                </v-row>

                <div style="text-align: center;">
                    {{ cards_accepted }}
                </div>
            </v-card-text>

        </v-card>
    </div>


</template>

<script>
import Validator from '@/js/validate.js';
import E164 from '@/js/E164.js';
import RcBaseInput from '@/components/shared/inputs/rc-base-input.vue';

export default {
    components: {
        RcBaseInput
    },

    props: {
        organization_label: {
            type: String,
            default: function () { return ''; }
        },

        payment: {
            type: Object,
            default: function () { return null; }
        },

        fluid: {
            type: Boolean,
            default: function () { return false; }
        },

        show_pay_button: {
            type: Boolean,
            default: function () { return true; }
        },
    },

    data() {
        return {
            plain_text_card_number: null,
            plain_text_ccv: null,
            name_on_card: null,
            expiration: null,
            billing_zip_code: null,
            age_verification_checked: false,
            age_verification_birthdate: null,
            email_address: null,
            phone_number: null,
            phone_input_valid: false,
            birthday_input_valid: false,
        }
    },

    methods: {

        nullToBlank(value) {
            if (value == null) return '';
            return value.trim();
        },

        onlyDigits(value) {
            value = this.nullToBlank(value);
            return value.replace(/\D/g, "");
        },

        expirationMonth(value) {
            let strs = value.split("/");
            if (strs.length == 0) return null;

            value = this.onlyDigits(strs[0]);
            if (value.length == 1) value = "0" + value;
            if (value.length < 2) return null;


            if (value == '01') return 'JANUARY';
            if (value == '02') return 'FEBRUARY';
            if (value == '03') return 'MARCH';
            if (value == '04') return 'APRIL';
            if (value == '05') return 'MAY';
            if (value == '06') return 'JUNE';
            if (value == '07') return 'JULY';
            if (value == '08') return 'AUGUST';
            if (value == '09') return 'SEPTEMBER';
            if (value == '10') return 'OCTOBER';
            if (value == '11') return 'NOVEMBER';
            if (value == '12') return 'DECEMBER';

            return null;
        },

        expirationMonthNum(value) {
            let strs = value.split("/");
            if (strs.length == 0) return null;
            value = this.onlyDigits(strs[0]);

            return parseInt(value);
        },


        expirationYear(value) {
            let strs = value.split("/");
            if (strs.length < 2) return null;

            value = this.onlyDigits(strs[1]);

            if (value.length == 2) {
                let year = 2000 + parseInt(value);
                return year;
            }
            else if (value.length == 4) {
                let year = parseInt(value);
                return year;
            }
        },

        onInput() {
            this.$nextTick(() => {
                this.$emit('input', this.createPaymentInfoCreditCard());
            });
        },

        isFieldRequired(field) {
            if (this.payment?.request == null) return false;

            if (field == 'email') return this.payment.request.ex_field_email === 'required';
            if (field == 'phone') return this.payment.request.ex_field_phone === 'required';

            return true; // all other fields required in this case
        },

        createPaymentInfoCreditCard() {
            let plain_text_card_number = this.onlyDigits(this.plain_text_card_number);
            if (plain_text_card_number.length < 13) return { 'error': 'Card number is invalid' };
            if (!this.isValidCardNumber(plain_text_card_number)) return { 'error': 'Card number is invalid' };

            let plain_text_ccv = this.onlyDigits(this.plain_text_ccv);
            if (plain_text_ccv.length < 3) return { 'error': 'CCV must be at least three digits' };

            let name_on_card = this.nullToBlank(this.name_on_card);
            if (name_on_card.length < 1) return { 'error': 'Name on card may not be blank' };

            let expiration_month = this.expirationMonth(this.expiration);
            let expiration_year = this.expirationYear(this.expiration);

            if (expiration_month == null) return { 'error': 'Invalid expiration date. Must be in the format MM/YY' };
            if (expiration_year == null) return { 'error': 'Invalid expiration date. Must be in the format MM/YY' };

            let email_address = this.email_address;
            if (email_address != null && (!email_address.includes('@') || !email_address.includes('.'))) email_address = null;

            if (this.payment.request.ex_field_email === 'required' && email_address == null) return { 'error': 'Valid email address required' };

            let phone_number_as_endpoint = E164.createEndpointFromUSPhoneNumber(this.phone_number, null);

            if (this.payment.request.ex_field_phone === 'required' && (phone_number_as_endpoint == null || !this.phone_input_valid)) return { 'error': 'Valid phone number required' };

            if (this.payment.request.ex_field_phone === 'optional' && phone_number_as_endpoint == null) {
                // check for non-blank but invalid phone number...
                if (this.phone_number != null && this.phone_number.trim().length > 0 && !this.phone_input_valid) {
                    return { 'error': 'The phone number field must contain a valid phone number' };
                }
            }

            // this is complicated... so, month number is 1 - 12 but date uses 0 - 11... so subtract one to get to javascript numbering and then add one to get the next month = no change
            // The day of zero goes back a month... confusing...
            var as_date = new Date(expiration_year, this.expirationMonthNum(this.expiration), 0);

            if (new Date().getTime() > as_date.getTime()) return { 'error': 'Card has expired.' };

            let billing_zip_code = this.billing_zip_code;
            if (billing_zip_code == null || billing_zip_code.length < 5) return { 'error': 'Billing zip / postal code must contain at least five letters or numbers' };

            let age_verification_provided_text = null;

            if (this.payment.request.age_verification_requirement === 'checkbox') {
                age_verification_provided_text = "checked";
            }

            if (this.payment.request.age_verification_requirement === 'birthdate') {
                age_verification_provided_text = this.age_verification_birthdate;

                if (this.age_verification_birthdate == null || !this.birthday_input_valid) {
                    return { 'error': 'You must provide a valid date of birth (MM/DD/YYYY)' };
                }
            }

            let min_age_in_years = this.min_age_in_years;

            if (this.payment.request.age_verification_requirement === 'checkbox' && !this.age_verification_checked) {
                return { 'error': 'You must check the box affirming you are over the age of ' + min_age_in_years + ' to complete payment' };
            }

            if (this.payment.request.age_verification_requirement === 'birthdate' && !Validator.isOverAgeInYears(min_age_in_years, this.age_verification_birthdate, new Date())) {
                return { 'error': "You must be over the age of " + min_age_in_years + " to complete payment" };
            }

            return {
                plain_text_card_number,
                plain_text_ccv,
                name_on_card,
                expiration_month,
                expiration_year,
                billing_zip_code,
                age_verification_provided_text,
                email_address,
                phone_number: phone_number_as_endpoint,
            };
        },

        isValidCardNumber(value) {
            if (/[^0-9-\s]+/.test(value)) return false;

            let nCheck = 0, bEven = false;
            value = value.replace(/\D/g, "");

            for (var n = value.length - 1; n >= 0; n--) {
                let cDigit = value.charAt(n);
                let nDigit = parseInt(cDigit, 10);

                if (bEven && (nDigit *= 2) > 9) nDigit -= 9;

                nCheck += nDigit;
                bEven = !bEven;
            }

            // Amex, ensure it's 15 digits
            // https://www.w3resource.com/javascript/form/credit-card-validation.php
            if (value.startsWith("34") || value.startsWith("37")) {
                if (value.length != 15) return false;
            }

            return (nCheck % 10) == 0;
        },

        validate() {
            let credit_card_info = this.createPaymentInfoCreditCard();

            if (credit_card_info.error != null) {
                return credit_card_info.error;
            }

            return null;
        },

        onPay() {
            let credit_card_info = this.createPaymentInfoCreditCard();

            if (credit_card_info.error != null) {
                this.$rc.snackbar(credit_card_info.error, 3500);
                return;
            }

            this.$emit('on-pay', credit_card_info);
        },

        focusPayButton() {
            // for use by the parent, to return focus to the pay button after a dialog closes
            this.$nextTick(() => {
                if (this.$refs['pay-button'] != null) {
                    this.$refs['pay-button'].$el.focus();
                }
            });
        }
    },

    computed: {
        payment_title() {
            if (this.payment.request.for_message != null && this.payment.request.for_message != '') {
                return 'Enter Payment Info';
            }
            else {
                return 'Pay by Card';
            }
        },

        payment_text() {
            if (this.payment.request.for_message != null && this.payment.request.for_message != '') {
                return 'Submit';
            }

            let ret = this.payment.request.amount_pretty_print;
            return 'Pay ' + ret;
        },

        pretty_print_amount_or_for_message() {
            if (this.payment.request.for_message != null && this.payment.request.for_message != '') {
                return this.payment.request.for_message;
            }

            let ret = this.payment.request.amount_pretty_print_without_currency;
            return ret;
        },

        cards_accepted() {
            let cards = [];

            let request = this.payment.request;

            if (request.accept_visa) cards.push('Visa');
            if (request.accept_mastercard) cards.push('Mastercard');
            if (request.accept_amex) cards.push('Amex');
            if (request.accept_discover) cards.push('Discover');

            let ret = '';

            for (let i = 0; i < cards.length; i++) {
                if (ret.length != 0 && i == cards.length - 1 && cards.length > 1) ret += ', and ';
                else if (ret.length != 0) ret += ', ';

                ret += cards[i];
            }

            return ret + ' cards accepted.';
        },

        min_age_in_years() {
            if (this.payment == null) return 21;
            if (this.payment.request == null) return 21;
            if (this.payment.request.min_age_in_years == null) return 21;

            return this.payment.request.min_age_in_years;
        },

        accept_ach() {
            return this.payment.request.accept_ach;
        },

        accept_credit() {
            return this.payment.request.accept_visa || this.payment.request.accept_mastercard || this.payment.request.accept_amex || this.payment.request.accept_discover;
        },

        show_email() {
            return this.payment.request.ex_field_email != 'do-not-show';
        },

        show_phone() {
            return this.payment.request.ex_field_phone != 'do-not-show';
        },
    },

    watch: {
        organization_label: {
            handler(newValue, oldValue) {
                if (newValue) {
                    document.title = `Pay by Card | ${newValue}`;
                }
            },
            immediate: true
        },

        expiration(newValue, oldValue) {
            if (newValue.trim().length == 2) {
                this.$nextTick(() => {
                    this.expiration = this.expiration + '/';
                });
            }

            if (this.expiration.indexOf('//') != -1) {
                this.$nextTick(() => {
                    let regex = /\/\//i;
                    this.expiration = this.expiration.replace(regex, '/');
                });
            }
        },

        plain_text_card_number(newValue, oldValue) {
            let val = '' + newValue;
            val = val.toLowerCase();

            if (val === 'test' || val === 'fill' || val === 'sample') {
                this.name_on_card = "John Consumer";
                this.plain_text_card_number = "378282246310005";
                this.plain_text_ccv = "112";
                this.expiration = "02/29";
                this.billing_zip_code = "13676";
                this.email_address = this.show_email ? 'john.doe@gmail.com' : null;
                this.phone_number = this.show_phone ? '237-448-1938' : null;

                if (this.payment.request.age_verification_requirement === 'birthdate') {
                    this.age_verification_birthdate = '01/01/1970';
                }

                this.onInput();
                this.$rc.$emit('payment-fill-sample');
            }
        },
    }
}
</script>

<style lang="scss" scoped>
.max_width {
    width: 550px;
}

.payment-heading {
    font-weight: inherit;
    font-size: inherit;
}

/*
    When used as text/bg with white text, our primary color is not AA compliant on a white bg.
    This should be moved to global.css eventually ...
    But that will necessitate more work than just these styles for consistency sake.
    Only applying to public forms for now.
*/
div.v-application .primary--text {
    color: var(--v-primary-darken1) !important;
    caret-color: var(--v-primary-darken1) !important;
}

div.v-application .error--text {
    color: var(--v-error-darken1) !important;
    caret-color: var(--v-error-darken1) !important;
}

div.v-alert.primary {
    background-color: var(--v-primary-darken1) !important;
    border-color: var(--v-primary-darken1) !important;
}

/* default light grey background is not sufficient on its own for AA accessibility */
:deep(.v-list-item--highlighted) {
    outline: 2px solid #000;
}

.v-btn--text {
    display: inline-block;
    white-space: normal;
}

.theme--light.v-btn:hover {
    opacity: 0.85 !important;

    &::before {
        opacity: 0 !important;
    }
}
</style>